import { DotTranslationKey } from '../../../../translations'
import { ArrayElement } from '../../utils/types'

export const MetricType = {
    // original values
    BloodOxygen: 'SPO2',
    BloodPressure: 'BP',
    BloodSugar: 'GLUCOSE',
    BodyHeight: 'HEIGHT',
    BodyMassIndex: 'BMI',
    BodyTemperature: 'TEMPERATURE',
    BodyWeight: 'WEIGHT',
    DailySteps: 'STEPS',
    HeartRateVariability: 'HRV',
    RestingHeartRate: 'RESTING_HR',
    SleepTime: 'SLEEP',

    // new values
    WaterIntake: 'WATER_INTAKE',
    MedicationIntake: 'MEDICATION_INTAKE',
    Mood: 'MOOD',

    // original pregnancy values
    FoetalHeartRate: 'FOETAL_HR',
    FoetalHeartRateVariability: 'FOETAL_HRV',
    UterineActivity: 'UTERINE',
    Arrhythmia: 'ARRHYTHMIA',
    AbdominalCircumference: 'ABDOMINAL_CIRCUMFERENCE',
} as const

export const metricTypeFilters: { value: MetricType; labelKey: DotTranslationKey }[] = [
    { value: MetricType.BloodPressure, labelKey: 'common.metricTypes.metricTypeBP' },
    { value: MetricType.BloodSugar, labelKey: 'common.metricTypes.metricTypeGLUCOSE' },
    { value: MetricType.BodyWeight, labelKey: 'common.metricTypes.metricTypeWEIGHT' },
    { value: MetricType.BodyHeight, labelKey: 'common.metricTypes.metricTypeHEIGHT' },
    { value: MetricType.BodyTemperature, labelKey: 'common.metricTypes.metricTypeTEMPERATURE' },
    { value: MetricType.RestingHeartRate, labelKey: 'common.metricTypes.metricTypeRESTING_HR' },
    { value: MetricType.BloodOxygen, labelKey: 'common.metricTypes.metricTypeSPO2' },
    { value: MetricType.DailySteps, labelKey: 'common.metricTypes.metricTypeSTEPS' },
    { value: MetricType.SleepTime, labelKey: 'common.metricTypes.metricTypeSLEEP' },
    { value: MetricType.HeartRateVariability, labelKey: 'common.metricTypes.metricTypeHRV' },
    { value: MetricType.Mood, labelKey: 'common.metricTypes.metricTypeMOOD' },
    { value: MetricType.WaterIntake, labelKey: 'common.metricTypes.metricTypeWATER_INTAKE' },
    {
        value: MetricType.MedicationIntake,
        labelKey: 'common.metricTypes.metricTypeMEDICATION_INTAKE',
    },

    { value: MetricType.FoetalHeartRate, labelKey: 'common.metricTypes.metricTypeFOETAL_HR' },
    {
        value: MetricType.FoetalHeartRateVariability,
        labelKey: 'common.metricTypes.metricTypeFOETAL_HRV',
    },
    { value: MetricType.UterineActivity, labelKey: 'common.metricTypes.metricTypeUTERINE' },
    { value: MetricType.Arrhythmia, labelKey: 'common.metricTypes.metricTypeARRHYTHMIA' },
    {
        value: MetricType.AbdominalCircumference,
        labelKey: 'common.metricTypes.metricTypeABDOMINAL_CIRCUMFERENCE',
    },
]

export const metricTypeMap: Record<MetricType, string[]> = {
    HEIGHT: ['bodyHeight', 'height'],
    WEIGHT: ['bodyWeight'],
    BMI: ['bmi'],
    TEMPERATURE: ['bodyTemperature'],
    BP: ['bloodPressureSystolic', 'bloodPressureDiastolic'],
    RESTING_HR: ['restingHeartRate'],
    SPO2: ['spo2'],
    GLUCOSE: ['glucoseAfterMeal', 'glucoseBeforeMeal'],
    STEPS: ['dailySteps'],
    SLEEP: ['sleep'],
    HRV: [],

    WATER_INTAKE: ['waterIntake'],
    MEDICATION_INTAKE: ['medicationIntake'],

    FOETAL_HR: [],
    FOETAL_HRV: [],
    UTERINE: [],
    ABDOMINAL_CIRCUMFERENCE: [],
    ARRHYTHMIA: [],
    MOOD: [],
}

export const metricTypeMapArr: Record<(typeof MetricType)[keyof typeof MetricType], string[]> = {
    [MetricType.BodyHeight]: ['bodyHeight', 'height'],
    [MetricType.BodyWeight]: ['bodyWeight'],
    [MetricType.BodyMassIndex]: ['bmi'],
    [MetricType.BodyTemperature]: ['bodyTemperature'],
    [MetricType.BloodPressure]: ['bloodPressureSystolic', 'bloodPressureDiastolic'],
    [MetricType.RestingHeartRate]: ['restingHeartRate'],
    [MetricType.BloodOxygen]: ['spo2'],
    [MetricType.BloodSugar]: ['glucoseAfterMeal', 'glucoseBeforeMeal'],
    [MetricType.DailySteps]: ['dailySteps'],
    [MetricType.SleepTime]: ['sleep'],
    [MetricType.HeartRateVariability]: [],
    [MetricType.WaterIntake]: ['waterIntake'],
    [MetricType.MedicationIntake]: ['medicationIntake'],
    [MetricType.FoetalHeartRate]: [],
    [MetricType.FoetalHeartRateVariability]: [],
    [MetricType.UterineActivity]: [],
    [MetricType.Arrhythmia]: [],
    [MetricType.AbdominalCircumference]: [],
    [MetricType.Mood]: [],
}

export type MetricType = (typeof MetricType)[keyof typeof MetricType]
type CustomMetricKeys = 'WaterIntake' | 'MedicationIntake'

export const CustomMetricMap: Pick<typeof MetricType, CustomMetricKeys> = {
    WaterIntake: MetricType.WaterIntake,
    MedicationIntake: MetricType.MedicationIntake,
} as const

export type CustomMetricType = (typeof CustomMetricMap)[CustomMetricKeys]

export type MetricJson = { value?: number; systolic?: number; diastolic?: number }

interface MetricBase {
    type: MetricType
    displayValue: string
}

export interface BloodOxygen extends MetricBase {
    type: typeof MetricType.BloodOxygen
    spO2: number
}

export interface BloodPressure extends MetricBase {
    type: typeof MetricType.BloodPressure
    diastolic: number
    systolic: number
}

export enum MealSituation {
    Fasting = 'fasting',
    BeforeMeal = 'before_meal',
    AfterMeal = 'after_meal',
    Random = 'random',
}

export interface BloodSugar extends MetricBase {
    type: typeof MetricType.BloodSugar
    mmolPerL: number
    mealSituation: MealSituation
}

export interface BodyHeight extends MetricBase {
    type: typeof MetricType.BodyHeight
    cm: number
}

export interface BodyMassIndex extends MetricBase {
    type: typeof MetricType.BodyMassIndex
    bmi: number
}

export interface BodyTemperature extends MetricBase {
    type: typeof MetricType.BodyTemperature
    celsius: number
}

export interface BodyWeight extends MetricBase {
    type: typeof MetricType.BodyWeight
    kg: number
}

export interface DailySteps extends MetricBase {
    type: typeof MetricType.DailySteps
    steps: number
}

export interface FoetalHeartRate extends MetricBase {
    type: typeof MetricType.FoetalHeartRate
    bpm: number
}

export interface FoetalHeartRateVariability extends MetricBase {
    type: typeof MetricType.FoetalHeartRateVariability
    ms: number
}

export interface HeartRateVariability extends MetricBase {
    type: typeof MetricType.HeartRateVariability
    ms: number
}

export interface RestingHeartRate extends MetricBase {
    type: typeof MetricType.RestingHeartRate
    bpm: number
}

export interface SleepTime extends MetricBase {
    type: typeof MetricType.SleepTime
    totalHours: number
}

export interface WaterIntake extends MetricBase {
    type: typeof MetricType.WaterIntake
    dl: number
}

export interface MedicationIntake extends MetricBase {
    type: typeof MetricType.MedicationIntake
    amount: number
}

export interface UterineActivity extends MetricBase {
    type: typeof MetricType.UterineActivity
}

interface Arrhythmia extends MetricBase {
    type: typeof MetricType.Arrhythmia
}

interface AbdominalCircumference extends MetricBase {
    type: typeof MetricType.AbdominalCircumference
    circumference: number
}

export type Metric =
    | BloodOxygen
    | BloodPressure
    | BloodSugar
    | BodyHeight
    | BodyMassIndex
    | BodyTemperature
    | BodyWeight
    | DailySteps
    | FoetalHeartRate
    | FoetalHeartRateVariability
    | HeartRateVariability
    | RestingHeartRate
    | SleepTime
    | WaterIntake
    | MedicationIntake
    | UterineActivity
    | Arrhythmia
    | AbdominalCircumference

export const metricTypesManualAllowed = [
    MetricType.BodyWeight,
    MetricType.BodyHeight,
    MetricType.BodyTemperature,
    MetricType.BloodPressure,
    MetricType.BloodSugar,
    MetricType.BloodOxygen,
    MetricType.WaterIntake,
    MetricType.MedicationIntake,
    MetricType.AbdominalCircumference,
] as const

export type MetricTypeManualAllowed = ArrayElement<typeof metricTypesManualAllowed>

export const isManualAllowed = (metricType: MetricType): metricType is MetricTypeManualAllowed =>
    metricTypesManualAllowed.includes(metricType as MetricTypeManualAllowed) // This is ugly TODO: Try without casting

export type MetricManualAllowed = Metric & { type: MetricTypeManualAllowed }

export const isDaily = (metricType: MetricType) =>
    ([MetricType.DailySteps, MetricType.SleepTime] as MetricType[]).includes(metricType)

export const MetricLevel = {
    Normal: 'NORMAL',
    WarningAbove: 'WARNING_ABOVE',
    WarningBelow: 'WARNING_BELOW',
    AlertAbove: 'ALERT_ABOVE',
    AlertBelow: 'ALERT_BELOW',
} as const

export type MetricLevel = (typeof MetricLevel)[keyof typeof MetricLevel]

const excludedMetricTypes: MetricType[] = [
    MetricType.FoetalHeartRate,
    MetricType.FoetalHeartRateVariability,
    MetricType.UterineActivity,
    MetricType.Arrhythmia,
    MetricType.AbdominalCircumference,
]

export const getMetricTypes = () => {
    return Object.values(MetricType).filter((type) => {
        return !excludedMetricTypes.includes(type)
    })
}

export const metricTypeUnits: Record<MetricType, string> = {
    [MetricType.BloodOxygen]: 'spO2',
    [MetricType.BloodPressure]: 'Hgmm',
    [MetricType.BloodSugar]: 'mmolPerL',
    [MetricType.BodyHeight]: 'cm',
    [MetricType.BodyMassIndex]: 'bmi',
    [MetricType.BodyTemperature]: 'celsius',
    [MetricType.BodyWeight]: 'kg',
    [MetricType.DailySteps]: 'steps',
    [MetricType.HeartRateVariability]: 'ms',
    [MetricType.RestingHeartRate]: 'bpm',
    [MetricType.SleepTime]: 'totalHours',

    [MetricType.WaterIntake]: 'dl',
    [MetricType.MedicationIntake]: '',

    [MetricType.AbdominalCircumference]: '',
    [MetricType.Arrhythmia]: '',
    [MetricType.FoetalHeartRate]: '',
    [MetricType.FoetalHeartRateVariability]: '',
    [MetricType.UterineActivity]: '',
    [MetricType.Mood]: '',
}

export const metricTypeUnitTranslations: Record<MetricType, string> = {
    [MetricType.BloodOxygen]: 'common.metricTypeUnits.oxygenSaturationUnit',
    [MetricType.BloodPressure]: 'common.metricTypeUnits.bloodPressureUnit',
    [MetricType.BloodSugar]: 'common.metricTypeUnits.bloodSugarUnit',
    [MetricType.BodyHeight]: 'common.metricTypeUnits.bodyHeightUnit',
    [MetricType.BodyMassIndex]: 'common.metricTypeUnits.bodyMassIndexUnit',
    [MetricType.BodyTemperature]: 'common.metricTypeUnits.bodyTemperatureUnit',
    [MetricType.BodyWeight]: 'common.metricTypeUnits.bodyWeightUnit',
    [MetricType.DailySteps]: 'common.metricTypeUnits.stepUnit',
    [MetricType.HeartRateVariability]: 'common.metricTypeUnits.heartRateVariabilityUnit',
    [MetricType.RestingHeartRate]: 'common.metricTypeUnits.restingHeartRateUnit',
    [MetricType.SleepTime]: 'common.metricTypeUnits.sleepUnit',
    [MetricType.WaterIntake]: 'common.metricTypeUnits.waterIntakeUnit',
    [MetricType.MedicationIntake]: '',
    [MetricType.AbdominalCircumference]: '',
    [MetricType.Arrhythmia]: '',
    [MetricType.FoetalHeartRate]: '',
    [MetricType.FoetalHeartRateVariability]: '',
    [MetricType.UterineActivity]: '',
    [MetricType.Mood]: '',
}

export const getUnitFromMetric = (metric: Metric): string => {
    const keys = Object.keys(metric).filter((k) => k !== 'type' && k !== 'displayValue')

    if (metric.type === MetricType.BloodPressure) {
        return 'Hgmm'
    }

    if (keys.length === 0) {
        return ''
    }

    return keys[0]
}

export const getMetricTypesTresholds = (): MetricType[] => {
    return Object.values(MetricType).filter(
        (type): type is MetricType =>
            !excludedMetricTypes.includes(type as MetricType) &&
            type !== MetricType.BodyHeight &&
            type !== MetricType.BodyMassIndex &&
            type !== MetricType.MedicationIntake,
    )
}
